import { watch } from 'vue'
import { pusherService } from '@/services/pusherService'
import { getSocketId } from '@/services/socketId'
import vuex from '@/store'

/**
 * Keeps the story list current from the API's story broadcasts (ND-2336),
 * replacing the /changes poll as the primary update path.
 *
 * One subscription per app, not per page: state.list is global and several
 * pages read it, so a per-component subscription would apply every event once
 * per open page.
 *
 * Nothing here fetches on receipt. The payload is applied straight to the
 * store — a fetch per event would turn one write into a read from every open
 * tab in the station, which is the cost this exists to remove.
 */
class StoryStream {
  constructor() {
    this.cleanupFns = []
    this.stopWatchers = []
    // The station we are bound to right now, and the last one we were ever
    // bound to. The difference is what tells a reconnect from a station switch.
    this.boundStation = null
    this.lastBoundStation = null
  }

  /**
   * Called once at app start. Idempotent — the watchers it installs drive
   * everything after that.
   */
  start() {
    if (this.stopWatchers.length) return

    this.stopWatchers.push(
      watch(
        () => vuex.getters.currentStation?.station,
        () => this.sync()
      ),
      watch(
        () => pusherService.isConnected.value,
        () => this.sync()
      )
    )

    this.sync()
  }

  stop() {
    this.unbind()
    this.lastBoundStation = null

    this.stopWatchers.forEach((stop) => stop())
    this.stopWatchers = []
  }

  /**
   * Reconcile the subscription with the connection and the station. Safe to
   * call as often as either changes; it no-ops when already correct.
   */
  sync() {
    const station = vuex.getters.currentStation?.station
    const want = pusherService.isConnected.value && station ? station : null

    if (want === this.boundStation) return

    // Rebinding the same station means we lost the socket and got it back. An
    // API deploy drops every connection, and anything published while we were
    // down is gone — nothing replays it, so pull once rather than leaving the
    // grid stale until the next poll tick.
    const isReconnect = want !== null && want === this.lastBoundStation

    this.unbind()

    if (!want) return

    this.bind(want)

    if (isReconnect) this.resync()
  }

  bind(station) {
    // The private- prefix is what makes Reverb authorize the subscribe; without
    // it this is a different, public channel that nothing publishes to.
    const channel = `private-stories.${station}`

    pusherService.subscribe(channel)

    for (const action of ['created', 'updated', 'deleted']) {
      this.cleanupFns.push(pusherService.on(channel, `story.${action}`, (event) => this.handle(event)))
    }

    this.boundStation = station
    this.lastBoundStation = station
    console.debug('[StoryStream] bound to', channel)
  }

  unbind() {
    this.cleanupFns.forEach((fn) => fn())
    this.cleanupFns = []

    if (this.boundStation) {
      pusherService.unsubscribe(`private-stories.${this.boundStation}`)
      this.boundStation = null
    }
  }

  handle(event) {
    // A subscription torn down mid-switch can still deliver one more event;
    // applying it would put another station's row in this station's list.
    if (event?.station_name !== vuex.getters.currentStation?.station) return

    // Our own write coming back. The API asks Reverb to leave this socket out
    // (toOthers), but Reverb only honours that on the instance that received
    // the publish, so behind more than one instance the echo usually gets
    // through. The writer already refreshes itself off the save response, and
    // applying the echo on top is what raised the "Story changed" dialog
    // against the editor's own title (ND-2387).
    if (event.origin_socket_id && event.origin_socket_id === getSocketId()) return

    vuex.dispatch('applyStoryBroadcast', event)
  }

  /**
   * getChanges rather than a straight refetch: it compares the station's
   * watermark against what we have already applied, so a reconnect after a
   * quiet minute costs one cached row and no list fetch at all.
   */
  resync() {
    vuex.dispatch('getChanges').catch((error) => {
      console.error('[StoryStream] resync after reconnect failed', error)
    })
  }
}

export const storyStream = new StoryStream()
